import type { UpdateTest } from "./connectivityTestTypes";

/**
 * General network and browser-environment probes: third-party services,
 * regional reachability, DNS, network-path shape, and local browser features.
 * These are blind to FreightSimple — they help tell a local network problem
 * apart from one on our side. See connectivityServerChecks for the FS-facing
 * probes.
 */

export async function testExternalServices(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const externalServices = [
      "https://httpbin.org/get",
      "https://jsonplaceholder.typicode.com/posts/1",
      "https://httpstat.us/200",
    ];

    const results = await Promise.allSettled(
      externalServices.map((url) =>
        fetch(url, {
          method: "HEAD",
          cache: "no-cache",
          signal: AbortSignal.timeout(10000),
        }),
      ),
    );

    const duration = performance.now() - startTime;

    const successfulRequests = results.filter(
      (r) => r.status === "fulfilled" && r.value.ok,
    ).length;

    const failureDetails = results
      .map((result, i) => {
        if (result.status === "fulfilled") {
          return result.value.ok
            ? `${externalServices[i]}: OK (${result.value.status})`
            : `${externalServices[i]}: HTTP ${result.value.status}`;
        }
        const error = result.reason;
        if (error.name === "AbortError") {
          return `${externalServices[i]}: Timeout`;
        } else if (
          error.name === "TypeError" &&
          error.message.includes("fetch")
        ) {
          return `${externalServices[i]}: Network Error`;
        }
        return `${externalServices[i]}: ${error.message || "Unknown Error"}`;
      })
      .join(", ");

    if (successfulRequests >= 2 && duration < 5000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 external services accessible in ${Math.round(duration)}ms`,
      });
    } else if (successfulRequests >= 1 && duration < 8000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 external services accessible in ${Math.round(duration)}ms (${failureDetails})`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "External services inaccessible",
        details: `${successfulRequests}/3 external services accessible in ${Math.round(duration)}ms (${failureDetails})`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "External services test failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testRegionalConnectivity(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const regionalServices = [
      { name: "US-East", url: "https://httpbin.org/delay/1" },
      { name: "US-West", url: "https://httpbin.org/delay/1" },
      { name: "EU", url: "https://httpbin.org/delay/1" },
    ];

    const results = await Promise.allSettled(
      regionalServices.map((service) =>
        fetch(service.url, {
          method: "HEAD",
          cache: "no-cache",
          signal: AbortSignal.timeout(15000),
        }).then((response) => ({ ...service, response })),
      ),
    );

    const duration = performance.now() - startTime;

    const successfulRequests = results.filter(
      (r) => r.status === "fulfilled" && r.value.response.ok,
    ).length;

    const regionalDetails = results
      .map((result, i) => {
        const serviceName = regionalServices[i]?.name ?? "Unknown";
        if (result.status === "fulfilled") {
          return result.value.response.ok
            ? `${serviceName}: OK (${result.value.response.status})`
            : `${serviceName}: HTTP ${result.value.response.status}`;
        }
        const error = result.reason;
        if (error.name === "AbortError") {
          return `${serviceName}: Timeout`;
        } else if (
          error.name === "TypeError" &&
          error.message.includes("fetch")
        ) {
          return `${serviceName}: Network Error`;
        }
        return `${serviceName}: ${error.message || "Unknown Error"}`;
      })
      .join(", ");

    if (successfulRequests >= 2 && duration < 8000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 regions accessible in ${Math.round(duration)}ms`,
      });
    } else if (successfulRequests >= 1 && duration < 12000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 regions accessible in ${Math.round(duration)}ms (${regionalDetails})`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "Regional connectivity issues",
        details: `${successfulRequests}/3 regions accessible in ${Math.round(duration)}ms (${regionalDetails})`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "Regional connectivity test failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testDnsResolution(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const testDomains = [
      "https://www.google.com/favicon.ico",
      "https://www.cloudflare.com/favicon.ico",
      "https://www.amazon.com/favicon.ico",
    ];

    const results = await Promise.allSettled(
      testDomains.map(
        (url) =>
          new Promise<{ success: boolean; duration: number }>((resolve) => {
            const img = new Image();
            const start = performance.now();

            img.onload = () => {
              resolve({ success: true, duration: performance.now() - start });
            };

            // Even if the image fails to load, DNS resolution succeeded if we
            // got this far.
            img.onerror = () => {
              resolve({ success: true, duration: performance.now() - start });
            };

            setTimeout(() => {
              resolve({ success: false, duration: 8000 });
            }, 8000);

            img.src = url;
          }),
      ),
    );

    const duration = performance.now() - startTime;

    const successfulRequests = results.filter(
      (r) => r.status === "fulfilled" && r.value.success,
    ).length;

    if (successfulRequests >= 2 && duration < 4000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 domains resolved in ${Math.round(duration)}ms`,
      });
    } else if (successfulRequests >= 1 && duration < 6000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 domains resolved in ${Math.round(duration)}ms`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "DNS resolution issues",
        details: `${successfulRequests}/3 domains resolved in ${Math.round(duration)}ms`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "DNS resolution test failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testNetworkPathAnalysis(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const startTime = performance.now();

    const networkTests = [
      {
        name: "CDN Edge",
        url: "https://httpbin.org/delay/0",
        type: "Global CDN",
      },
      {
        name: "Regional CDN",
        url: "https://httpbin.org/delay/0",
        type: "Regional",
      },
      { name: "Direct API", url: "https://httpstat.us/200", type: "Direct" },
    ];

    const results = await Promise.allSettled(
      networkTests.map((test) =>
        fetch(test.url, {
          method: "HEAD",
          cache: "no-cache",
          signal: AbortSignal.timeout(12000),
        }).then((response) => ({ ...test, response })),
      ),
    );

    const duration = performance.now() - startTime;

    const successfulRequests = results.filter(
      (r) => r.status === "fulfilled" && r.value.response.ok,
    ).length;

    let analysis = "";
    if (successfulRequests === 0) {
      analysis = "Complete network failure - likely local network issue";
    } else if (successfulRequests === 1) {
      analysis = "Partial connectivity - possible routing/firewall issue";
    } else if (successfulRequests === 2) {
      analysis = "Mostly working - minor network issues";
    } else {
      analysis = "Network path working normally";
    }

    if (successfulRequests >= 2 && duration < 6000) {
      updateTest(index, {
        status: "passed",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 network paths working (${analysis})`,
      });
    } else if (successfulRequests >= 1 && duration < 10000) {
      updateTest(index, {
        status: "warning",
        duration: Math.round(duration),
        details: `${successfulRequests}/3 network paths working (${analysis})`,
      });
    } else {
      updateTest(index, {
        status: "failed",
        duration: Math.round(duration),
        error: "Network path analysis failed",
        details: `${successfulRequests}/3 network paths working (${analysis})`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "Network path analysis failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}

export async function testBrowserEnvironment(
  index: number,
  updateTest: UpdateTest,
): Promise<void> {
  updateTest(index, { status: "running" });

  try {
    const issues: string[] = [];

    try {
      localStorage.setItem("connectivity-test", "test");
      localStorage.removeItem("connectivity-test");
    } catch {
      issues.push("localStorage not available");
    }

    try {
      sessionStorage.setItem("connectivity-test", "test");
      sessionStorage.removeItem("connectivity-test");
    } catch {
      issues.push("sessionStorage not available");
    }

    if (typeof window === "undefined") {
      issues.push("JavaScript execution environment not available");
    }

    if (issues.length === 0) {
      updateTest(index, {
        status: "passed",
        details: "All browser features working correctly",
      });
    } else {
      updateTest(index, {
        status: "warning",
        details: `Issues detected: ${issues.join(", ")}`,
      });
    }
  } catch (error) {
    updateTest(index, {
      status: "failed",
      error: "Browser environment test failed",
      details: error instanceof Error ? error.message : "Unknown error",
    });
  }
}
